import type { FormEvent } from 'react'
import { s, h } from '../lib/sx'
import { A } from '../lib/router'
import { PageHero } from '../components/PageHero'
import { copy, quoteOptions } from '../content/copy'

const label = s("font-family:'IBM Plex Mono',monospace;font-size:10.5px;letter-spacing:0.18em;text-transform:uppercase;color:#7E8894;display:block;margin-bottom:10px;")
const field = s('width:100%;padding:15px 16px;border:1px solid #C3C9D1;background:#FFFFFF;font-size:15px;outline:none;')
const primary = s('background:#1F5C99;color:#FFFFFF;border:0;padding:16px 34px;font-size:14px;font-weight:600;cursor:pointer;')
const secondary = s('background:transparent;color:#1B2430;border:1px solid #C3C9D1;padding:16px 28px;font-size:14px;font-weight:600;cursor:pointer;')

const STEP_LABELS = ['Project basics', 'Scope & budget', 'Your details']

export interface QuoteDraft {
  name?: string
  work?: string
  budget?: string
}

export interface QuoteProps {
  step: number
  setStep: (n: number) => void
  draft: QuoteDraft
  setDraft: (patch: Partial<QuoteDraft>) => void
  submitted: boolean
  setSubmitted: (v: boolean) => void
}

export function Quote({ step, setStep, draft, setDraft, submitted, setSubmitted }: QuoteProps) {
  /** Not wired to a backend — see README.md § Forms. */
  const submit = (e: FormEvent) => {
    e.preventDefault()
    setSubmitted(true)
  }

  return (
    <div>
      <PageHero eyebrow="Request a quote" title={copy.quote.headline} sub={copy.quote.sub} />

      <section style={s('max-width:900px;margin:0 auto;padding:clamp(48px,7vw,64px) clamp(18px,4vw,32px) 110px;')}>
        {submitted ? (
          <div style={s('border-top:2px solid #A8500F;padding-top:36px;')}>
            <h2 style={s('font-family:Archivo,sans-serif;font-weight:800;font-size:42px;letter-spacing:-0.03em;margin:0 0 18px;')}>{copy.quote.submittedHeading}</h2>
            <p style={s('font-size:18px;line-height:1.65;color:#38424E;margin:0 0 28px;max-width:620px;')}>{copy.quote.submittedBody}</p>
            <A href="/projects" style={s('display:inline-block;background:#1B2430;color:#F2F4F6;padding:15px 28px;font-size:14px;font-weight:600;')} className={h('background:#1F5C99;color:#FFFFFF;')}>
              Browse our projects
            </A>
          </div>
        ) : (
          <div>
            <div style={s('display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,150px),1fr));gap:20px;margin-bottom:48px;')}>
              {[1, 2, 3].map((n) => (
                <div key={n}>
                  <div style={s(`height:3px;background:${n <= step ? '#1F5C99' : '#DCE0E5'};`)} />
                  <div style={s("font-family:'IBM Plex Mono',monospace;font-size:10.5px;letter-spacing:0.16em;text-transform:uppercase;color:#7E8894;margin-top:14px;")}>Step {n}</div>
                  <div style={s(`font-family:Archivo,sans-serif;font-weight:600;font-size:16px;color:${n <= step ? '#1B2430' : '#7E8894'};margin-top:6px;`)}>{STEP_LABELS[n - 1]}</div>
                </div>
              ))}
            </div>

            {step === 1 && (
              <div style={s('display:flex;flex-direction:column;gap:22px;')}>
                <div>
                  <label style={label} htmlFor="q-name">
                    Project name or reference
                  </label>
                  <input id="q-name" value={draft.name ?? ''} onChange={(e) => setDraft({ name: e.target.value })} style={field} />
                </div>
                <div>
                  <span style={label}>Type of work</span>
                  <div style={s('display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,250px),1fr));gap:1px;background:#DCE0E5;border:1px solid #DCE0E5;')}>
                    {quoteOptions.workTypes.map((w) => {
                      const active = draft.work === w
                      return (
                        <button
                          key={w}
                          onClick={() => setDraft({ work: w })}
                          aria-pressed={active}
                          style={s(`background:${active ? '#1B2430' : '#F2F4F6'};color:${active ? '#F2F4F6' : '#1B2430'};border:0;padding:20px 16px;font-size:14.5px;font-weight:500;text-align:left;cursor:pointer;`)}
                          className={h('background:#E7EAEE;')}
                        >
                          {w}
                        </button>
                      )
                    })}
                  </div>
                </div>
                <div style={s('display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,200px),1fr));gap:16px;')}>
                  <div>
                    <label style={label} htmlFor="q-province">
                      Province
                    </label>
                    <select id="q-province" style={field}>
                      {quoteOptions.provinces.map((p) => (
                        <option key={p}>{p}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={label} htmlFor="q-start">
                      Target start
                    </label>
                    <select id="q-start" style={field}>
                      {quoteOptions.starts.map((o) => (
                        <option key={o}>{o}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <button onClick={() => setStep(2)} style={s('align-self:flex-start;background:#1F5C99;color:#FFFFFF;border:0;padding:16px 34px;font-size:14px;font-weight:600;cursor:pointer;margin-top:8px;')} className={h('background:#17497A;')}>
                  Continue →
                </button>
              </div>
            )}

            {step === 2 && (
              <div style={s('display:flex;flex-direction:column;gap:22px;')}>
                <div>
                  <label style={label} htmlFor="q-scope">
                    Describe the scope
                  </label>
                  <textarea
                    id="q-scope"
                    placeholder="Quantities, drawings available, known constraints, live operations on site…"
                    rows={7}
                    style={s('width:100%;padding:15px 16px;border:1px solid #C3C9D1;background:#FFFFFF;font-size:15px;outline:none;resize:vertical;')}
                  />
                </div>
                <div>
                  <span style={label}>Indicative budget</span>
                  <div style={s('display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,170px),1fr));gap:1px;background:#DCE0E5;border:1px solid #DCE0E5;')}>
                    {quoteOptions.budgets.map((b) => {
                      const active = draft.budget === b
                      return (
                        <button
                          key={b}
                          onClick={() => setDraft({ budget: b })}
                          aria-pressed={active}
                          style={s(`background:${active ? '#1B2430' : '#F2F4F6'};color:${active ? '#F2F4F6' : '#1B2430'};border:0;padding:18px 10px;font-family:'IBM Plex Mono',monospace;font-size:12px;cursor:pointer;`)}
                          className={h('background:#E7EAEE;')}
                        >
                          {b}
                        </button>
                      )
                    })}
                  </div>
                </div>
                <div>
                  <label style={label} htmlFor="q-form">
                    Contract form
                  </label>
                  <select id="q-form" style={field}>
                    {quoteOptions.contractForms.map((c) => (
                      <option key={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div style={s('display:flex;gap:12px;margin-top:8px;')}>
                  <button onClick={() => setStep(1)} style={secondary} className={h('border-color:#1B2430;')}>
                    ← Back
                  </button>
                  <button onClick={() => setStep(3)} style={primary} className={h('background:#17497A;')}>
                    Continue →
                  </button>
                </div>
              </div>
            )}

            {step === 3 && (
              <form onSubmit={submit} style={s('display:flex;flex-direction:column;gap:22px;')}>
                <div style={s('display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,200px),1fr));gap:16px;')}>
                  <div>
                    <label style={label} htmlFor="q-fullname">
                      Full name
                    </label>
                    <input id="q-fullname" required style={field} />
                  </div>
                  <div>
                    <label style={label} htmlFor="q-org">
                      Organisation
                    </label>
                    <input id="q-org" required style={field} />
                  </div>
                </div>
                <div style={s('display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,200px),1fr));gap:16px;')}>
                  <div>
                    <label style={label} htmlFor="q-email">
                      Email
                    </label>
                    <input id="q-email" type="email" required style={field} />
                  </div>
                  <div>
                    <label style={label} htmlFor="q-phone">
                      Phone
                    </label>
                    <input id="q-phone" required style={field} />
                  </div>
                </div>
                <div style={s('background:#DCE0E5;padding:24px 26px;font-size:14px;line-height:1.65;color:#38424E;')}>
                  {copy.quote.attachmentsBefore}
                  <span style={s("font-family:'IBM Plex Mono',monospace;")}>{copy.quote.attachmentsEmail}</span>
                  {copy.quote.attachmentsAfter}
                </div>
                <div style={s('display:flex;gap:12px;')}>
                  <button type="button" onClick={() => setStep(2)} style={secondary} className={h('border-color:#1B2430;')}>
                    ← Back
                  </button>
                  <button type="submit" style={s('background:#1F5C99;color:#FFFFFF;border:0;padding:16px 40px;font-size:14px;font-weight:600;cursor:pointer;')} className={h('background:#17497A;')}>
                    Submit request
                  </button>
                </div>
              </form>
            )}
          </div>
        )}
      </section>
    </div>
  )
}
